/* agmf-card-runtime.js — shared placement lifecycle for feed utility cards.
 *
 * A card owns its content; this runtime owns the repeated mechanics around it:
 * recipe position/every, waiting for lazy feed cycles, the maximum instance
 * count, end-of-feed fallback, observer cleanup and duplicate-start defense.
 * Position is a count of six-card feed cycles: 0 = top, 1 = after one cycle.
 *
 * Add another card type with:
 *   AGMF_CARD_RUNTIME.mount({
 *     id: 'unique-feed-card', section, recipe,
 *     defaultPosition: 1, defaultEvery: 0, maxCards: 3,
 *     create: function (index) { return cardElement; },
 *     onUpdate: function () { ...optional repaint... }
 *   });
 *
 * ONE CARD SHOWING PER GAP (Dorian 2026-10-12: the sale card sat "just after
 * a Free Shipping card ... its too similar so what we do so they won't be one
 * next to the other"). Every utility card lands in a gap between two cycles,
 * and two cards whose settings name the same gap stack there - measured on
 * every new page: free shipping {1, every 3} and the sale {1, every 2} met
 * after the first cycle and again after the seventh. Cards still go to the gap
 * their settings name; what changed is that a gap SHOWS one card - the first
 * one in it that is showing - and any later one there steps aside
 * (data-agmf-yield, hidden by agilite-media-flow.css).
 * It is re-decided whenever a card shows or hides, not at placement, because
 * whether a card shows is often known only later: the delivery card is empty
 * (hidden) outside the shipped countries or on a sold-out product, and the
 * sale card hides on a full-price variant. A shopper who never gets the
 * delivery card therefore still sees the sale card in the gap it was set for.
 * One exception to "the first": a card the shopper is using (data-agmf-hold,
 * set by the sale card once its "Choose options" is pressed) keeps the gap -
 * picking an in-stock variant brings the delivery card back into that gap,
 * and the card in the shopper's hand must not vanish for it (2026-10-13).
 * Any feed-level .agmf-card counts, whoever placed it.
 *
 * Loaded once on every product page. Kill switch:
 * window.AGMF_CARD_RUNTIME_OFF = true.
 */
(function () {
  'use strict';
  if (window.AGMF_CARD_RUNTIME || window.AGMF_CARD_RUNTIME_OFF) return;

  var mounts = {};

  function nonNegative(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : Math.max(0, parsed);
  }

  function call(fn) {
    if (typeof fn !== 'function') return;
    try { fn(); } catch (e) { /* one card cannot break the feed */ }
  }

  /* ---- one card showing per gap (see the header) ------------------------- */

  var YIELD = 'data-agmf-yield';
  // a card the shopper is using: it keeps its gap when another card there
  // starts showing (the sale card sets it once its "Choose options" is pressed:
  // the in-stock variant chosen brings the free-shipping card back into that
  // same gap, and the card in the shopper's hand must not vanish for it)
  var HOLD = 'data-agmf-hold';

  // A feed-level `.agmf-card` is always a utility card: media cards live
  // INSIDE cycles.
  function isCard(el) {
    return !!(el && el.classList && el.classList.contains('agmf-card') &&
              !el.classList.contains('agmf-cycle'));
  }

  // Walk the feed once: a cycle closes a gap; inside a gap ONE showing card
  // keeps it - a held one (HOLD) if any, else the first - and every other
  // showing card steps aside. A card the owner hid (`hidden`) is not showing
  // and takes nothing.
  function decide(gap) {
    var keep = null, i;
    for (i = 0; i < gap.length && !keep; i++) if (!gap[i].hidden && gap[i].hasAttribute(HOLD)) keep = gap[i];
    for (i = 0; i < gap.length && !keep; i++) if (!gap[i].hidden) keep = gap[i];
    for (i = 0; i < gap.length; i++) {
      var step = !gap[i].hidden && gap[i] !== keep;
      if (step !== gap[i].hasAttribute(YIELD)) {
        if (step) gap[i].setAttribute(YIELD, '');
        else gap[i].removeAttribute(YIELD);
      }
    }
  }
  function settle(feed) {
    var gap = [];
    for (var el = feed.firstElementChild; el; el = el.nextElementSibling) {
      if (el.classList && el.classList.contains('agmf-cycle')) { decide(gap); gap = []; continue; }
      if (isCard(el)) gap.push(el);
    }
    decide(gap);
  }

  // One watcher per feed, whichever card mounts first. Cards arriving, and a
  // card's own `hidden` going on or off, are the only things that change a
  // gap; changes inside a cycle (the recycler, a dead picture) are ignored.
  var watched = [];
  function watchGaps(feed) {
    if (!feed || watched.indexOf(feed) >= 0) return;
    watched.push(feed);
    settle(feed);
    if (!window.MutationObserver) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        // a child added/removed on the feed itself, or `hidden`/HOLD on one of its cards
        var gap = r.type === 'childList' ? r.target === feed
                                         : isCard(r.target) && r.target.parentNode === feed;
        if (gap) { settle(feed); return; }
      }
    }).observe(feed, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', HOLD] });
  }

  function mount(options) {
    var opts = options || {};
    var id = String(opts.id || '').trim();
    if (!id) return null;
    if (mounts[id]) return mounts[id];

    var section = opts.section || null;
    var feed = opts.feed || (section && section.querySelector('[data-agmf-feed]'));
    var sentinel = opts.sentinel || (section && section.querySelector('[data-agmf-sentinel]'));
    var maxCards = Math.max(1, nonNegative(opts.maxCards, 1));
    var recipe = null;
    var recipeResolved = false;
    var nextAt = null;
    var every = 0;
    var placed = 0;
    var stopped = false;
    var feedObserver = null;
    var sentinelObserver = null;

    var controller = {
      id: id,
      disabled: !feed || typeof opts.create !== 'function',
      refresh: tick,
      stop: stop,
      getPlacedCount: function () { return placed; }
    };
    mounts[id] = controller;
    if (controller.disabled) return controller;

    function resolveRecipe() {
      if (recipeResolved) return !controller.disabled;
      recipeResolved = true;
      try {
        recipe = typeof opts.recipe === 'function' ? (opts.recipe() || {}) : (opts.recipe || {});
      } catch (e) { recipe = {}; }
      controller.disabled = recipe.enabled === false;
      nextAt = nonNegative(recipe.position, nonNegative(opts.defaultPosition, 0));
      every = nonNegative(recipe.every, nonNegative(opts.defaultEvery, 0));
      return !controller.disabled;
    }

    function make(index) {
      try { return opts.create(index); }
      catch (e) { return null; }
    }

    function placedOne(card, index) {
      if (typeof opts.onPlaced === 'function') {
        try { opts.onPlaced(card, index); } catch (e) {}
      }
    }

    function placeDue(cycles) {
      if (stopped || placed >= maxCards || nextAt === Infinity) return;
      while (!stopped && placed < maxCards && nextAt < cycles.length) {
        var index = placed;
        var card = make(index);
        if (!card) { stop(); return; }
        feed.insertBefore(card, cycles[nextAt]);
        placed++;
        placedOne(card, index);
        if (!every) { nextAt = Infinity; break; }
        nextAt += every;
      }
    }

    function placeAtEndIfNeverPlaced() {
      if (stopped) return;
      if (placed === 0) {
        var card = make(0);
        if (card) {
          feed.appendChild(card);
          placed = 1;
          placedOne(card, 0);
        }
      }
      nextAt = Infinity;
      stop();
    }

    function atEnd() {
      return !!(sentinel && sentinel.classList && sentinel.classList.contains('agmf-sentinel--end'));
    }

    function tick() {
      if (stopped) return;
      var cycles = feed.querySelectorAll('.agmf-cycle');
      var ended = atEnd();
      // window.AGMF_FEED is assigned immediately before the engine creates
      // its first cycle. Resolve a recipe only at that point, not when this
      // deferred asset happens to execute earlier in the page.
      if (!recipeResolved && !cycles.length && !ended) {
        call(opts.onUpdate);
        return;
      }
      if (!resolveRecipe()) { stop(); return; }
      placeDue(cycles);
      call(opts.onUpdate);
      if (ended) placeAtEndIfNeverPlaced();
      else if (placed >= maxCards || nextAt === Infinity) stop();
    }

    function stop() {
      if (stopped) return;
      stopped = true;
      if (feedObserver) feedObserver.disconnect();
      if (sentinelObserver) sentinelObserver.disconnect();
      call(opts.onUpdate);
    }

    watchGaps(feed);
    tick();
    if (!stopped && window.MutationObserver) {
      feedObserver = new MutationObserver(tick);
      feedObserver.observe(feed, { childList: true });
      if (sentinel) {
        sentinelObserver = new MutationObserver(tick);
        sentinelObserver.observe(sentinel, { attributes: true, attributeFilter: ['class'] });
      }
    }

    return controller;
  }

  window.AGMF_CARD_RUNTIME = {
    version: 1,
    mount: mount,
    get: function (id) { return mounts[id] || null; }
  };
})();
